<template>
    <div>
        <UiCardListHeader
            :title="lang('admissions::entries.widgets.latest.title')"
            :action-href="canViewAll ? indexUrl : undefined"
            :action-label="canViewAll ? lang('admissions::entries.widgets.latest.show_all') : undefined"
        />

        <UiCardEmpty v-if="entries.length === 0">
            {{ lang('admissions::entries.widgets.latest.empty') }}
        </UiCardEmpty>

        <UiCardList v-else class="space-y-2">
            <UiCard
                v-for="entry in entries"
                :key="entry.id"
                interactive
                as="a"
                :href="entry.edit_url"
                class="flex items-center justify-between gap-3 p-3"
            >
                <div class="min-w-0 flex-1">
                    <div class="flex items-baseline gap-2">
                        <span class="font-medium truncate">{{ entry.children_summary || '—' }}</span>
                        <span v-if="entry.children_count > 0" class="text-xs text-gray-500">
                            ({{ entry.children_count }}×)
                        </span>
                    </div>
                    <div class="text-xs text-gray-500 truncate">
                        {{ entry.parent_name }} · {{ entry.parent_email }}
                    </div>
                </div>

                <div class="flex flex-col items-end gap-1 shrink-0">
                    <UiBadge :color="paymentStatusColor(entry.payment_status)">
                        {{ lang(`admissions::entries.payment_status.${entry.payment_status}`) }}
                    </UiBadge>
                    <span class="text-xs text-gray-400">
                        {{ dateHumanReadable(entry.created_at) }}
                    </span>
                </div>
            </UiCard>
        </UiCardList>
    </div>
</template>

<script lang="ts" setup>
import { ref, Ref } from 'vue';
import { lang } from '@/lang/composable';
import { dateHumanReadable } from '@src/Common/Helpers/dates';

interface LatestEntry {
    id: number;
    children_summary: string;
    children_count: number;
    parent_name: string;
    parent_email: string;
    created_at: string | null;
    payment_status: string;
    payment_method: string | null;
    edit_url: string;
}

const props = defineProps<{ slug: string; settings?: object }>();

const entries: Ref<LatestEntry[]> = ref([]);
const indexUrl = ref<string>('');
const canViewAll = ref<boolean>(false);

const paymentStatusColor = (status: string): string => {
    switch (status) {
        case 'paid':
            return 'green';
        case 'overpaid':
            return 'blue';
        case 'underpaid':
            return 'yellow';
        case 'created':
        default:
            return 'gray';
    }
};

const load = () => {
    axios
        .get(route('admin.dashboards.widgets.data', { slug: props.slug }))
        .then(({ data }) => {
            entries.value = data.entries;
            indexUrl.value = data.index_url;
            canViewAll.value = data.can_view_all ?? false;
        });
};

load();
</script>
